import React, { useEffect, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Avatar from '@material-ui/core/Avatar';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';

import adminApi from '../../Api/adminApi';
import { USER_MESSAGES_REVERTED } from '../../shared/UserMessaging';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';
import { KICKER } from '../Home/HomeDashboard/dashboardStyles';
import useAdminStyles from './adminStyles';
import StateCard from './StateCard';
import StatusChip from './StatusChip';
import ManagementActions from './ManagementActions';
import { isForbiddenError } from './useAdminQuery';
import { fmtDate, fmtDateTime, fmtInterval, fmtMoney, fmtNumber, fullName, isLessonLive, toDateParam } from './adminFormat';

const USAGE_DAYS = 90;

const useStyles = makeStyles(() => ({
    paper: {
        width: '640px',
        maxWidth: '100vw',
        padding: '24px 26px 40px',
        boxSizing: 'border-box',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        marginBottom: '18px',
    },
    name: {
        margin: 0,
        fontSize: '1.3rem',
        lineHeight: 1.2,
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    email: {
        margin: '2px 0 0',
        color: REDESIGN_COLORS.body,
        overflowWrap: 'anywhere',
    },
    section: {
        marginBottom: '18px',
    },
    kicker: {
        ...KICKER,
        marginBottom: '8px',
    },
    kv: {
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, 160px) minmax(0, 1fr)',
        gap: '6px 14px',
        fontSize: '0.9rem',
        '& dt': {
            color: REDESIGN_COLORS.muted,
        },
        '& dd': {
            margin: 0,
            color: REDESIGN_COLORS.dark,
            overflowWrap: 'anywhere',
        },
    },
    tableWrap: {
        overflowX: 'auto',
    },
}));

const Section = ({ title, children }) => {
    const classes = useStyles();
    const admin = useAdminStyles();
    return (
        <section className={classes.section}>
            <span className={classes.kicker}>{title}</span>
            <div className={admin.card} style={{ padding: '14px 16px' }}>
                {children}
            </div>
        </section>
    );
};

const KV = ({ rows }) => {
    const classes = useStyles();
    return (
        <dl className={classes.kv}>
            {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value === null || value === undefined || value === '' ? '—' : value}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
};

const Empty = ({ children }) => {
    const admin = useAdminStyles();
    return <span className={admin.muted}>{children}</span>;
};

// Compact two-column count list used for tools and AI endpoints.
const CountTable = ({ rows, nameLabel, extra }) => (
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>{nameLabel}</TableCell>
                <TableCell align="right">Count</TableCell>
                {extra ? <TableCell align="right">{extra.label}</TableCell> : null}
            </TableRow>
        </TableHead>
        <TableBody>
            {rows.map((row) => (
                <TableRow key={row.name}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{fmtNumber(row.count)}</TableCell>
                    {extra ? <TableCell align="right">{fmtNumber(row[extra.id])}</TableCell> : null}
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

/**
 * First-party usage of one account over the last USAGE_DAYS days: lessons
 * (Postgres), whiteboard tools and AI calls (BigQuery). Loaded separately from
 * the account detail so a slow or unconfigured BigQuery never blocks the drawer.
 */
const UsageSection = ({ userId }) => {
    const admin = useAdminStyles();
    const [usage, setUsage] = useState(null);
    const [state, setState] = useState({ isLoading: false, isError: false });

    useEffect(() => {
        if (!userId) {
            setUsage(null);
            return undefined;
        }
        let active = true;
        setUsage(null);
        setState({ isLoading: true, isError: false });
        const from = new Date();
        from.setDate(from.getDate() - USAGE_DAYS);
        adminApi
            .getUserUsage(userId, { from: toDateParam(from) })
            .then((res) => {
                if (active) {
                    setUsage(res.data);
                    setState({ isLoading: false, isError: false });
                }
            })
            .catch(() => {
                if (active) {
                    setState({ isLoading: false, isError: true });
                }
            });
        return () => {
            active = false;
        };
    }, [userId]);

    const title = `Usage (last ${USAGE_DAYS} days)`;
    if (!usage) {
        return (
            <Section title={title}>
                <StateCard isLoading={state.isLoading} isError={state.isError} />
            </Section>
        );
    }
    if (usage.configured === false) {
        return (
            <Section title={title}>
                <Empty>BigQuery analytics is not configured on this server (see GCP/ANALYTICS.md).</Empty>
            </Section>
        );
    }
    const lessons = usage.lessons || {};
    const totalEvents = (usage.categories || []).reduce((acc, c) => acc + c.count, 0);
    return (
        <Section title={title}>
            <KV
                rows={[
                    ['Lessons started', fmtNumber(lessons.lessonsStarted)],
                    ['Lessons joined', fmtNumber(lessons.lessonsJoined)],
                    ['Minutes in lessons', fmtNumber(lessons.lessonMinutes)],
                    ['Events', fmtNumber(totalEvents)],
                    ['Active days', fmtNumber((usage.daily || []).length)],
                ]}
            />
            <div style={{ marginTop: '12px' }}>
                <strong style={{ fontSize: '0.9rem' }}>Whiteboard tools</strong>
                {usage.tools.length === 0 ? (
                    <div>
                        <Empty>No tool selections recorded.</Empty>
                    </div>
                ) : (
                    <CountTable rows={usage.tools} nameLabel="Tool" />
                )}
            </div>
            <div style={{ marginTop: '12px' }}>
                <strong style={{ fontSize: '0.9rem' }}>AI</strong>
                {usage.ai.length === 0 ? (
                    <div>
                        <Empty>No AI calls recorded.</Empty>
                    </div>
                ) : (
                    <CountTable rows={usage.ai} nameLabel="Endpoint" extra={{ id: 'errors', label: 'Errors' }} />
                )}
            </div>
            <p className={admin.muted} style={{ margin: '12px 0 0', fontSize: '0.8rem' }}>
                Tools and AI come from first-party events (collected since the pipeline went live); lessons come
                from the lesson records.
            </p>
        </Section>
    );
};

function messageLabel(row) {
    const code = USER_MESSAGES_REVERTED[row.messageCode] || `code ${row.messageCode}`;
    const meta = row.meta || {};
    const extra = [meta.subscriptionName, meta.endDate ? `until ${fmtDate(meta.endDate)}` : null]
        .filter(Boolean)
        .join(' ');
    return extra ? `${code} (${extra})` : code;
}

/**
 * Everything the admin needs about one account: identity, effective plan and
 * status, usage counters, plan controls, then the history of plan changes,
 * billing messages, logins, lessons and Stripe records.
 */
const UserDetailDrawer = ({ userId, open, onClose, onUpdated }) => {
    const classes = useStyles();
    const admin = useAdminStyles();
    const [detail, setDetail] = useState(null);
    const [state, setState] = useState({ isLoading: false, isError: false, isForbidden: false });

    useEffect(() => {
        if (!userId) {
            setDetail(null);
            return undefined;
        }
        let active = true;
        setDetail(null);
        setState({ isLoading: true, isError: false, isForbidden: false });
        adminApi
            .getUser(userId)
            .then((res) => {
                if (active) {
                    setDetail(res.data);
                    setState({ isLoading: false, isError: false, isForbidden: false });
                }
            })
            .catch((err) => {
                if (active) {
                    setState({ isLoading: false, isError: !isForbiddenError(err), isForbidden: isForbiddenError(err) });
                }
            });
        return () => {
            active = false;
        };
    }, [userId]);

    const handleUpdated = (next) => {
        setDetail(next);
        onUpdated();
    };

    const sub = detail?.subscription;
    const prefs = detail?.prefs;

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <div className={classes.head}>
                {detail?.avatar ? (
                    <Avatar src={detail.avatar} style={{ width: 48, height: 48 }} />
                ) : (
                    <AccountCircle style={{ width: 48, height: 48, color: '#B5C4C6' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 className={classes.name}>{detail ? fullName(detail) || detail.email : 'User'}</h2>
                    {detail ? <p className={classes.email}>{detail.email}</p> : null}
                </div>
                <IconButton aria-label="close" onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            {!detail ? (
                <StateCard isLoading={state.isLoading} isError={state.isError} isForbidden={state.isForbidden} />
            ) : (
                <>
                    <Section title="Subscription">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <strong style={{ fontSize: '1.1rem' }}>{detail.effectivePlan || '—'}</strong>
                            <StatusChip status={detail.status} />
                        </div>
                        <KV
                            rows={[
                                ['Plan ends', fmtDate(sub?.endDate, 'never')],
                                ['Started', fmtDate(sub?.startDate)],
                                ['Canceled at', fmtDate(sub?.canceledAt, 'not canceled')],
                                ['Trial used', sub?.trialStarted ? 'Yes' : 'No'],
                                [
                                    'Stripe customer',
                                    <span key="customer" className={admin.mono}>
                                        {prefs?.customerStripeId || '—'}
                                    </span>,
                                ],
                                [
                                    'Stripe subscription',
                                    <span key="subscription" className={admin.mono}>
                                        {sub?.stripeCustomerSubscriptionId || '—'}
                                    </span>,
                                ],
                            ]}
                        />
                    </Section>

                    <Section title="Plan controls">
                        <ManagementActions detail={detail} onUpdated={handleUpdated} />
                    </Section>

                    <Section title="Account">
                        <KV
                            rows={[
                                [
                                    'User id',
                                    <span key="id" className={admin.mono}>
                                        {detail.id}
                                    </span>,
                                ],
                                ['Joined', fmtDateTime(detail.createdAt)],
                                ['Billing email', prefs?.email],
                                ['Email valid', prefs ? (prefs.emailIsValid ? 'Yes' : 'No') : null],
                                ['Language', prefs?.language],
                                ['Groups', detail.groupCount],
                                ['Library folders', detail.collectionFolderCount],
                                ['Library boards', detail.libraryCount],
                                ['Linked Digitaliada', detail.linkedAccount?.digitaliadaEmail],
                                [
                                    'Samsung devices',
                                    detail.samsungDevices?.length
                                        ? detail.samsungDevices.map((d) => d.SN).join(', ')
                                        : null,
                                ],
                            ]}
                        />
                    </Section>

                    <UsageSection userId={detail.id} />

                    <Section title={`Plan history (${detail.history.length})`}>
                        {detail.history.length === 0 ? (
                            <Empty>No plan changes recorded.</Empty>
                        ) : (
                            <div className={classes.tableWrap}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell>Plan</TableCell>
                                            <TableCell>Ends</TableCell>
                                            <TableCell>Canceled</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.history.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{fmtDateTime(row.createdAt)}</TableCell>
                                                <TableCell>{row.subscriptionName || '—'}</TableCell>
                                                <TableCell>{fmtDate(row.endDate, 'never')}</TableCell>
                                                <TableCell>{fmtDate(row.canceledAt)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </Section>

                    <Section title={`Billing messages (${detail.messages.length})`}>
                        {detail.messages.length === 0 ? (
                            <Empty>No billing messages.</Empty>
                        ) : (
                            <div className={classes.tableWrap}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell>Event</TableCell>
                                            <TableCell>Invoice</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.messages.map((row) => (
                                            <TableRow key={row.id} style={row.isDeleted ? { opacity: 0.55 } : undefined}>
                                                <TableCell>{fmtDateTime(row.createdAt)}</TableCell>
                                                <TableCell>{messageLabel(row)}</TableCell>
                                                <TableCell>
                                                    {row.meta?.hostedUrl ? (
                                                        <a
                                                            className={admin.link}
                                                            href={row.meta.hostedUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Open
                                                        </a>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </Section>

                    <Section title={`Stripe records (${detail.stripeEvents.length})`}>
                        {detail.stripeEvents.length === 0 ? (
                            <Empty>No Stripe events stored for this customer.</Empty>
                        ) : (
                            <div className={classes.tableWrap}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell>Event</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Invoice</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.stripeEvents.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{fmtDateTime(row.createdAt)}</TableCell>
                                                <TableCell>{row.eventType}</TableCell>
                                                <TableCell align="right">{fmtMoney(row.amount, row.currency)}</TableCell>
                                                <TableCell>{row.status || '—'}</TableCell>
                                                <TableCell>
                                                    {row.hostedUrl ? (
                                                        <a
                                                            className={admin.link}
                                                            href={row.hostedUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Open
                                                        </a>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </Section>

                    <Section title={`Recent logins (${detail.logins.length})`}>
                        {detail.logins.length === 0 ? (
                            <Empty>No logins recorded.</Empty>
                        ) : (
                            <div className={classes.tableWrap}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>When</TableCell>
                                            <TableCell>Method</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.logins.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{fmtDateTime(row.logintime)}</TableCell>
                                                <TableCell>{row.authtype || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </Section>

                    <Section title={`Recent lessons (${detail.lessons.length})`}>
                        {detail.lessons.length === 0 ? (
                            <Empty>No lesson sessions recorded.</Empty>
                        ) : (
                            <div className={classes.tableWrap}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Started</TableCell>
                                            <TableCell>Class</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Duration</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.lessons.map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{fmtDateTime(row.starttime)}</TableCell>
                                                <TableCell>{row.name || row.classid}</TableCell>
                                                <TableCell>{row.isadmin === 1 ? 'Teacher' : 'Participant'}</TableCell>
                                                <TableCell>
                                                    {fmtInterval(row.duration)}
                                                    {isLessonLive(row) ? ' · LIVE' : ''}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </Section>
                </>
            )}
        </Drawer>
    );
};

export default UserDetailDrawer;
